'use client'

import * as React from 'react'
import { ListChecks, Banknote, Ruler, BedDouble, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Listing } from '@/lib/types'

// The 4-lens metric switcher overlaid on the map — design search.jsx:150-258
// (METRICS array + the frosted pill bar + the bottom-left hint caption).
// Each lens decides what the price-pill markers say.

export type MetricLensId = 'musthave' | 'price' | 'area' | 'rooms'

export interface MetricLens {
  id: MetricLensId
  icon: LucideIcon
  label: string
  hint: string
  pinText: (l: Listing) => string
  /** Optional extra line for the hover mini-card — design search.jsx:163-167. */
  sub?: (l: Listing) => string
}

type Translate = (key: string, fallback?: string) => string

// Keyword ids → readable ("pets_allowed" → "Pets allowed"); same fallback
// formatting as lib/feature-labels.ts, plus the prototype's capitalization.
const prettyKeyword = (k: string) => {
  const s = k.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Lens set — design search.jsx:162-171. The must-have lens only exists when
// the user has configured must-have dealbreakers (Settings → Search defaults).
export function buildMetricLenses(mustHaves: string[], t: Translate): MetricLens[] {
  const met = (l: Listing) => mustHaves.filter((k) => (l.keywords || []).includes(k))
  const lenses: MetricLens[] = []

  if (mustHaves.length > 0) {
    lenses.push({
      id: 'musthave',
      icon: ListChecks,
      label: t('search.lensMustHave', 'Must-have'),
      hint: t('search.lensMustHaveHint', 'how many of your must-haves each home meets'),
      pinText: (l) => `${met(l).length}/${mustHaves.length}`,
      sub: (l) => {
        const has = met(l)
        const missed = mustHaves.filter((k) => !has.includes(k))
        return (
          (has.length
            ? `${t('search.lensHas', 'Has')} ${has.map(prettyKeyword).join(', ')}`
            : t('search.lensNoMustHaves', 'None of your must-haves')) +
          (missed.length
            ? ` · ${t('search.lensMissing', 'missing')} ${missed.map(prettyKeyword).join(', ')}`
            : '')
        )
      },
    })
  }

  lenses.push(
    {
      id: 'price',
      icon: Banknote,
      label: 'Kč',
      hint: t('search.lensPriceHint', 'monthly rent · thousands Kč'),
      pinText: (l) => `${Math.round(l.price / 1000)}k`,
    },
    {
      id: 'area',
      icon: Ruler,
      label: 'm²',
      hint: t('search.lensAreaHint', 'apartment size'),
      pinText: (l) => (typeof l.area === 'number' ? `${l.area}m²` : '—'),
    },
    {
      id: 'rooms',
      icon: BedDouble,
      label: t('search.lensRooms', 'Rooms'),
      hint: t('search.lensRoomsHint', 'rooms + kitchenette'),
      pinText: (l) => (typeof l.rooms === 'number' ? `${l.rooms}+kk` : '—'),
    }
  )

  return lenses
}

// Frosted pill bar — design search.jsx:243-255 (+ lensBtn at 189-194).
export interface MapMetricLensProps {
  lenses: MetricLens[]
  value: MetricLensId
  onChange: (id: MetricLensId) => void
  className?: string
}

export function MapMetricLens({ lenses, value, onChange, className }: MapMetricLensProps) {
  return (
    <div
      className={cn(
        'flex max-w-[calc(100%-24px)] flex-wrap gap-[2px] rounded-full border border-outline-variant',
        'bg-surface/[0.88] p-1 shadow-e1 backdrop-blur-[8px]',
        className
      )}
    >
      {lenses.map((m) => {
        const on = m.id === value
        const Icon = m.icon
        return (
          <button
            key={m.id}
            type="button"
            title={m.hint}
            onClick={() => onChange(m.id)}
            className={cn(
              'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-[11px] py-[6px] text-[12px] font-bold',
              '[transition:background_0.18s,color_0.18s]',
              on ? 'bg-[#1C1917] text-white' : 'bg-transparent text-ink-secondary'
            )}
          >
            <Icon size={14} className={on ? 'text-white' : 'text-ink-muted'} />
            {m.label}
          </button>
        )
      })}
    </div>
  )
}
